import {
  addFilterSetting,
  checkFilterSetting,
  deleteFilterSettingById,
  getFilterSettingListByGame,
} from '@op/services';
import { gameInfoType } from '@op/types/common';
import { throttle } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import useLanguage from './useLanguage';
import { notification } from 'tea-component';

type FilterItemType = {
  filter_id: number;
  filter_name: string;
  filter_info: string;
};

export default function useSaveFilter({
  gameInfo,
  businessType,
  otherFilter = {},
  needRequest = false,
}: {
  gameInfo: gameInfoType;
  businessType?: number;
  otherFilter?: any;
  needRequest?: boolean;
}) {
  const { formatMessage } = useLanguage();
  const [filterName, setFilterName] = useState('');
  const [tipsMessage, setTipsMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterList, setFilterList] = useState<FilterItemType[]>([]);
  const deleteFilterItem = useRef<{ text: string; value: number }>();
  const [visible, setVisible] = useState(false);

  const verifyName = useCallback(
    throttle(async (name: string) => {
      const { edition_unified_id: uid, id_type: uid_type } = gameInfo;
      const filter: any = {
        uid,
        uid_type,
        filter_name: name,
      };
      if (businessType) {
        filter.business_type = businessType;
      }

      checkFilterSetting(filter)
        .then(() => {
          setTipsMessage('');
        })
        .catch((res: any) => {
          if (res?.code === 30009) {
            setTipsMessage(
              formatMessage({
                id: 'A setting with the same name already exists, click Save to replace this setting.',
              }),
            );
          }
        });
    }, 1000),
    [gameInfo],
  );

  const saveFilter = (filterCond: any, callback: () => void) => {
    const { edition_unified_id: uid, id_type: uid_type } = gameInfo;

    setLoading(true);
    const filter: any = {
      uid,
      uid_type,
      filter_name: filterName,
      filter_cond: JSON.stringify(filterCond),
    };

    if (businessType) {
      filter.business_type = businessType;
    }
    addFilterSetting(filter)
      .then(() => {
        notification.success({
          description: formatMessage({ id: 'Save successfully' }),
        });

        callback();
      })
      .catch(() => {
        notification.success({
          description: formatMessage({ id: 'Save Failed' }),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function getFilterList() {
    if (!gameInfo) return null;
    setLoading(true);
    const filter = {
      uid: gameInfo.edition_unified_id,
      uid_type: gameInfo.id_type,
      ...otherFilter,
    };

    if (businessType) {
      filter.business_type = businessType;
    }
    setFilterList([]);
    return getFilterSettingListByGame(filter)
      .then((data: any) => {
        setFilterList(data || []);
      })
      .catch(() => {
        setFilterList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function deleteFilter(requestList = false) {
    const id = deleteFilterItem.current?.value;
    if (!id) return;
    const { edition_unified_id: uid, id_type: uid_type } = gameInfo;
    const paramsData = { uid, uid_type, filter_id: id };
    setLoading(true);
    deleteFilterSettingById(paramsData)
      .then(() => {
        notification.success({
          description: formatMessage({ id: 'Delete Success' }),
        });
        closeDeleteWindow();
        if (requestList) {
          getFilterList();
        }
      })
      .catch(() => {
        notification.error({
          description: formatMessage({ id: 'Delete Failed' }),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function openDeleteWindow(item: { text: string; value: number }) {
    deleteFilterItem.current = item;
    setVisible(true);
  }

  function closeDeleteWindow() {
    setVisible(false);
    deleteFilterItem.current = undefined;
  }

  useEffect(() => {
    if (needRequest) {
      getFilterList();
    }
  }, []);

  return {
    verifyName,
    filterName,
    setFilterName,
    setTipsMessage,
    tipsMessage,
    saveFilter,
    loading,
    filterList,
    getFilterList,
    deleteFilter,
    visible,
    closeDeleteWindow,
    openDeleteWindow,
  };
}
